import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Autocomplete,
} from "@mui/material";
import "./feedbackAssignModal.css";
import {
  getFeedbackStatusList,
  getSupportAssigneeFromID,
} from "../../../features/manageFeedbacks/manageFeedbacksService";
import { Assignee, AssignSubmitRequestData } from "../../../features/manageFeedbacks/manageFeedbacksTypes";
import { TranslationEnum, usePortalTranslation } from "../../../providers/TranslationProvider";

interface AssignModalProps {
  feedbackData: Record<string, any>;
  open: boolean;
  onClose: () => void;
  onConfirm: (data: AssignSubmitRequestData) => void;
  supportAssignees: Assignee[];
}

const AssignModal: React.FC<AssignModalProps> = ({
  feedbackData,
  open,
  onClose,
  onConfirm,
  supportAssignees,
}) => {
  const { translate } = usePortalTranslation();
  const initialSupportAssignee = getSupportAssigneeFromID(supportAssignees, feedbackData.immediate_id);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [assignee, setAssignee] = React.useState<Assignee | null>(
    initialSupportAssignee || null
  );
  const [status, setStatus] = React.useState(feedbackData.status || null);
  const [remarks, setRemarks] = React.useState(feedbackData.remarks || "");

  const [assigneeError, setAssigneeError] = React.useState(
    feedbackData.immediate_id ? false : true
  );
  const [statusError, setStatusError] = React.useState(
    feedbackData.status ? false : true
  );
  const [remarksError, setRemarksError] = React.useState(
    feedbackData.remarks ? false : true
  );
  // Validation Check for Submit Button
  // const isSubmitDisabled = !assignee || !status || !remarks.trim();

  const feedbackStatusList = getFeedbackStatusList();
  const feedbackAssigneeList = supportAssignees;;
  const handleSubmit = () => {
    setIsSubmitAttempted(true);

    // Validate inputs
    const isAssigneeValid = !!assignee && (!!assignee.name || !!assignee.loginId);
    const isStatusValid = !!status;
    const isRemarksValid = !!remarks.trim();

    // Set error states
    setAssigneeError(!isAssigneeValid);
    setStatusError(!isStatusValid);
    setRemarksError(!isRemarksValid);

    if (isAssigneeValid && isStatusValid && isRemarksValid) {
      const data = {
        status: status,
        immediateId: assignee.loginId,
        remarks: remarks,
        supportId: feedbackData.support_id,
      };
      onConfirm(data);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{translate(TranslationEnum.manage_feedbacks, "Assign Feedback")}</DialogTitle>
      <DialogContent>
        <div className="feedbackAssignModal-assigneeInfoContainer">
          <div className="feedbackAssignModal-supportIdWrapper">
            <FormControl margin="normal">
              <TextField
                label={translate(TranslationEnum.manage_feedbacks,"User ID")}
                value={feedbackData.support_id || ""}
                variant="outlined"
                disabled
              />
            </FormControl>
          </div>
          <div className="feedbackAssignModal-assigneeInputWrapper">
            <Autocomplete
              sx={{ width: "100%" }}
              value={assignee || undefined} // set to the selected assignee object
              disableClearable
              onChange={(event, newValue) => {
                setAssignee(newValue); // Set the entire assignee object
                setAssigneeError(!newValue?.loginId); // Update error state based on selection
              }}
              options={feedbackAssigneeList} // Use the array of objects
              getOptionLabel={(option) => option.name} // Display the name in the dropdown
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate(TranslationEnum.manage_feedbacks,"Assigned To")}
                  required
                  error={assigneeError && isSubmitAttempted}
                  helperText={assigneeError && isSubmitAttempted ? translate(TranslationEnum.manage_feedbacks,"Assignee is required.") : ""}
                />
              )}
            />
          </div>
        </div>

        <FormControl fullWidth margin="normal">
          <TextField
            label={translate(TranslationEnum.manage_feedbacks,"Complaint")}
            value={feedbackData?.extended_attributes?.complaint || ""} // Placeholder for complaint text
            variant="outlined"
            multiline
            rows={2}
            disabled
          />
        </FormControl>

        <Autocomplete
          sx={{ width: "100%" }}
          value={status || ""} // Set value to empty string if status is undefined
          disableClearable
          onChange={(event, newValue) => {
            setStatus(newValue);
            setStatusError(!newValue); // Update error state based on selection
          }}
          options={feedbackStatusList} // Adjust with actual options
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label={translate(TranslationEnum.manage_feedbacks,"Status")}
              error={statusError && isSubmitAttempted}
              helperText={statusError && isSubmitAttempted ? translate(TranslationEnum.manage_feedbacks,"Status is required.") : ""}
            />
          )}
        />
        <FormControl fullWidth margin="normal">
          <TextField
            label={translate(TranslationEnum.manage_feedbacks,"Remarks")}
            value={remarks === "" ? "" : remarks} // Placeholder for remarks
            onChange={(e) => {
              setRemarks(e.target.value);
              setRemarksError(!e.target.value.trim()); // Update error state based on input
            }}
            variant="outlined"
            multiline
            rows={4}
            required
            error={remarksError && isSubmitAttempted}
            helperText={remarksError && isSubmitAttempted ? translate(TranslationEnum.manage_feedbacks,"Remarks are required.") : ""}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <div
          onClick={onClose}
          className="manageFeedback-cancel-button mangageFeedbacks-modalCancelButton"
        >
          {translate(TranslationEnum.common_portal,"Cancel")}
        </div>
        <div
          onClick={() => {
            handleSubmit();
          }}
          className="mangageFeedbacks-modalSubmitButton 
             manageFeedbacks-active-button"
          
        >
          {translate(TranslationEnum.common_portal,"Submit")}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AssignModal;
